import merge from 'lodash/merge';
import React, { isValidElement, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ReactElement, ReactNode, Ref, SyntheticEvent } from 'react';
import Draggable from 'react-draggable';
import { useSearchParams } from 'react-router-dom';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { removeDialog } from 'app-components/flux/root-ui/rootUiActions';
import { tracker } from 'app-components/utils/core';

import Dialog from '@mui/material/Dialog';
import Paper from '@mui/material/Paper';
import type { PaperProps } from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import { useTheme } from '@mui/material/styles';
import type { TransitionProps } from '@mui/material/transitions';

import StdDialogTitle from 'components/Dialogs/StdDialogTitle';
import ErrorBoundary from 'components/ErrorBoundary';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useUrlDialog } from 'hooks/useUrlDialog';

import useStyles from './styles';
import { DIALOG_CLOSE_EVENTS } from './types';
import type { DialogCloseEvent, StdDialogMinHeight, StdDialogProps, StdDialogSize } from './types';

export type {
  DialogCloseEvent,
  StdDialogCloseHandler,
  StdDialogCloseReason,
  StdDialogHandle,
  StdDialogProps,
  StdDialogSize,
} from './types';

// This component should be used for all Dialogs. It uses the material-ui Dialog

const FullScreenTransition = React.forwardRef(function FullScreenTransition(
  props: TransitionProps & { children: ReactElement<unknown> },
  ref: Ref<unknown>,
) {
  return <Slide direction='up' ref={ref} {...props} />;
});

export const isDialogCloseAction = (reason: string): reason is DialogCloseEvent =>
  DIALOG_CLOSE_EVENTS.some((event) => event === reason);

// MUI reports camelCase reasons; StdDialog reports constants and passes other reasons through.
const toCloseReason = (reason?: string | null): string => {
  if (!reason || reason === 'backdropClick') {
    return 'BACKDROP_CLICK';
  }
  if (reason === 'enterKeyDown') {
    return 'ENTER_KEY_DOWN';
  }
  if (reason === 'escapeKeyDown') {
    return 'ESCAPE_KEY_DOWN';
  }
  return reason;
};

// The plain text of a title, for analytics.
const getText = (node: ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') {
    return node.toString();
  }
  if (Array.isArray(node)) {
    return node.map(getText).join('');
  }
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return getText(node.props.children);
  }
  return '';
};

function DraggablePaperComponent(props: PaperProps): ReactElement {
  const paperRef = useRef<HTMLDivElement>(null);
  return (
    <Draggable nodeRef={paperRef} handle='.draggable-dialog-title'>
      <Paper {...props} ref={paperRef} />
    </Draggable>
  );
}

const StdDialog = (props: StdDialogProps): ReactElement => {
  const theme = useTheme();

  const {
    resolve,
    children,
    TitleIcon,
    classes: ferryClasses,
    colorBar = theme.palette.background.paper,
    type,
    id,
    dialogId,
    disableBackdropClick,
    fullScreen,
    guideId,
    minHeightSize = 'xs',
    open,
    onBack,
    onOpen,
    onClose,
    showCloseButton = true,
    size = 'sm',
    title,
    titleProps,
    sharedcomponentid,
    showBackButton = false,
    stdDialogRef,
    paperProps,
    showDivider = true,
    // fullWidth and maxWidth stay in otherProps so callers can override the size.
    ...otherProps
  } = props;

  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [closed, setClosed] = useState(false);
  const closeReasonRef = useRef('');

  const dragDialog = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'dragDialog'));
  const { classes, cx } = useStyles();

  const getMainId = useCallback(() => `stddialog-${dialogId}-root`, [dialogId]);
  const { closeDialog: closeUrlDialog } = useUrlDialog();

  const closeDialog = useCallback(
    (reason?: string) => {
      setClosed(true);
      setTimeout(() => dispatch(removeDialog(dialogId)), 200);
      if (searchParams.get('dialogs')) {
        closeUrlDialog(dialogId);
      }
      resolve?.(reason);
    },
    [dialogId, searchParams, resolve, dispatch, closeUrlDialog],
  );

  const handleClose = useCallback(
    (event: SyntheticEvent | null = null, reason?: string) => {
      // Callers closing through the ref can pass a stand-in event.
      event?.stopPropagation?.();

      // since mui v5 removed 'disableBackdropClick' support - handle it manually
      if (disableBackdropClick && reason === 'backdropClick') {
        return;
      }

      const closeReason = toCloseReason(reason);
      closeReasonRef.current = closeReason;
      if (onClose) {
        onClose(event, closeReason);
      } else {
        closeDialog(closeReason);
      }
    },
    [disableBackdropClick, onClose, closeDialog],
  );

  useImperativeHandle(stdDialogRef, () => ({ closeDialog, getMainId, handleClose }), [
    closeDialog,
    getMainId,
    handleClose,
  ]);

  const localPaperProps: PaperProps = !fullScreen
    ? {}
    : {
        sx: {
          '&.MuiPaper-root': {
            borderRadius: '0 !important',
          },
        },
      };

  const paperHeightClasses: Partial<Record<StdDialogSize, string>> = {
    xs: classes.paperHeightXs,
    sm: classes.paperHeightSm,
    md: classes.paperHeightMd,
    lg: classes.paperHeightLg,
  };
  const minHeightClasses: Record<StdDialogMinHeight, string> = {
    xs: classes.minHeightXs,
    sm: classes.minHeightSm,
    md: classes.minHeightMd,
    lg: classes.minHeightLg,
  };

  const isOpen = open === undefined ? !closed : open;
  // biome-ignore lint/correctness/useExhaustiveDependencies: listed deps decide when this runs; dialogId, id, onOpen and others would run it more often
  useEffect(() => {
    onOpen?.();
    if (type || dialogId || title) {
      tracker.track(tracker.events.modal, {
        type,
        id,
        dialogId,
        reason: closeReasonRef.current,
        title: getText(title),
        open: isOpen,
        modal: 'dialog',
      });
    }
  }, [isOpen]);

  return (
    <Dialog
      id={id}
      aria-labelledby='draggable-dialog-title'
      classes={{
        ...(!fullScreen && {
          paper: cx(paperHeightClasses[size], minHeightClasses[minHeightSize]),
        }),
        ...ferryClasses,
      }}
      fullScreen={fullScreen}
      fullWidth={size !== 'custom'}
      maxWidth={size === 'custom' ? undefined : size}
      open={isOpen}
      PaperComponent={dragDialog ? DraggablePaperComponent : undefined}
      PaperProps={{ ...merge(localPaperProps, paperProps), id: getMainId() }}
      TransitionComponent={fullScreen ? FullScreenTransition : undefined}
      {...otherProps}
      // MUI types the event as {}; it is the backdrop click or the Escape keydown.
      onClose={(event: SyntheticEvent, reason) => handleClose(event, reason)}
    >
      <ErrorBoundary>
        {!fullScreen && (title || showBackButton || showCloseButton) && (
          <StdDialogTitle
            {...titleProps}
            colorBar={colorBar}
            onBack={onBack}
            onClose={handleClose}
            guideId={guideId}
            sharedcomponentid={sharedcomponentid || 'STD_DIALOG'}
            showBackButton={showBackButton}
            showCloseButton={showCloseButton}
            showDivider={showDivider}
            sx={{ cursor: dragDialog ? 'move' : undefined, display: 'flex', alignItems: 'center', ...titleProps?.sx }}
          >
            {TitleIcon}
            {title ?? ''}
          </StdDialogTitle>
        )}
        {children}
      </ErrorBoundary>
    </Dialog>
  );
};

export default StdDialog;
